import React, { useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { useApiListProvider } from '../../../../../api-new/state/auth'
import { useRoleSysProvider } from '../../../../../role/hook/role-sys'
import { IconActivate, IconAdd, IconDomain, IconProvider, IconRemove, IconUnactivate } from '../../../../../shared/icon/icon'
import { FleetPage, FleetSection, Notice } from '../../../../../shared/sso-workspace/fleet-page'
import { FleetGrid, useGridList } from '../../../../../shared/sso-workspace/fleet-grid'
import { TableAgClient } from '../../../../../shared/table-builder/table-ag-client'
import { ROW_HEIGHT_TWO_LINE } from '../../../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnMenu, ColumnSelector, ColumnText } from '../../../../../shared/table-builder/table-columns'
import { Menu, MenuItem } from '../../../../../shared/menu/menu'
import { Pill } from '../../../../../shared/sso-workspace/pill'
import { ProviderLogo } from '../../../../../shared/sso-workspace/provider-logo'
import { ConfirmDialog } from '../../../../../shared/sso-workspace/confirm-dialog'
import { authPut } from '../../../../../shared/sso-workspace/auth-api'
import { serverMessage } from '../../../../../shared/sso-workspace/format'
import { connectionKind, connectionOwner } from '../../provider/page/provider-list'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'

const name = row => row.displayName || row.providerName

// The generic PUT replaces the row with the body it's given. Toggling Active
// sends the row as it was read, minus what that PUT must never touch: a
// present `domains` list replaces the connection's bindings, and `public`
// has its own endpoint.
export const activeBody = (row, active) => {
  const body = { ...row, active }
  delete body.domains
  delete body.pendingDomains
  delete body.public
  return body
}

// Sign-in methods (audit B3): the buttons offered to EVERYONE on the sign-in
// page, whatever their email. Being on that list used to be an accident --
// any active connection with no bound domain was on it, including a school's
// connection that had just been added or had lost its last domain. Now it is
// a deliberate property of the connection (providers.public), set here and
// nowhere else:
//
//   - Public sign-in methods: the connections marked public, each switched
//     on or off, and "Stop offering to everyone". A public method never
//     signs in an existing account it isn't connected to straight away:
//     the person confirms the account once (SSO link confirmation), which
//     the section and "Offer to everyone?" say.
//   - Not offered to anyone: connections with no domain bound and no public
//     flag -- nobody can reach them. Staff either bind a domain (it's a
//     school's connection) or, deliberately and with a warning, make it
//     public (it's a platform method like Google, Apple or Facebook).
export const SigninMethodList = () => {
  const apiListProvider = useApiListProvider()
  const roleSysProvider = useRoleSysProvider()
  const canEdit = Boolean(roleSysProvider.accessUpdate)

  const [rows, setRows] = useState(undefined)
  const [status, setStatus] = useState('')
  const [error, failure, setError] = useErrorLine()
  const [confirm, setConfirm] = useState(null) // { row, public }
  const [confirmError, confirmFailure, setConfirmError] = useErrorLine()
  const [busy, setBusy] = useState(false)

  // Both grids show the same list, loaded (and reloaded, from either grid's
  // footer) through their onReady. Mounting readies both, so the first grid
  // to be ready loads it and the other's first ready does not load it again;
  // a load already in flight answers any call made meanwhile.
  const inflight = useRef(null)
  const publicTableRef = useRef()
  const unreachableTableRef = useRef()
  const load = () => {
    if (!inflight.current) {
      inflight.current = (async () => {
        try {
          setRows((await apiListProvider.get()) || [])
        } catch (err) {
          setRows(null)
        } finally {
          inflight.current = null
        }
      })()
    }
    return inflight.current
  }

  const readied = useRef(new Set())
  const readyFor = grid => () => {
    if (!readied.current.has(grid)) {
      readied.current.add(grid)
      if (readied.current.size > 1) return inflight.current || Promise.resolve()
    }
    return load()
  }

  const publicRows = useMemo(() => (Array.isArray(rows) ? rows.filter(r => r.public) : rows), [rows])
  const unreachable = useMemo(() => (Array.isArray(rows) ? rows.filter(r => !r.public && !(r.domains || []).length) : rows), [rows])

  const toggleActive = async row => {
    setError(null)
    setStatus('')
    setBusy(true)
    try {
      await apiListProvider.put(row.id, activeBody(row, !row.active))
      setStatus(row.active ? `${name(row)} is switched off. Nobody can sign in with it.` : `${name(row)} is switched on and offered to everyone.`)
      await load()
    } catch (err) {
      setError(serverMessage(err, `${name(row)} couldn't be ${row.active ? 'switched off' : 'switched on'}.`), err)
    } finally {
      setBusy(false)
    }
  }

  const setPublic = async () => {
    const { row } = confirm
    const next = confirm.public
    setBusy(true)
    setConfirmError(null)
    try {
      await authPut(`provider/${row.id}/public`, { public: next })
    } catch (err) {
      setConfirmError(serverMessage(err, `${name(row)} couldn't be changed.`), err)
      setBusy(false)
      return
    }
    setBusy(false)
    setConfirm(null)
    setStatus(next
      ? `${name(row)} is now a public sign-in method${row.active ? ' and appears on the sign-in page for everyone' : '. Switch it on to offer it'}.`
      : `${name(row)} is no longer offered to everyone. People reach it only through a bound domain.`)
    await load()
  }

  // The cells' words, flattened onto the row so the grids sort, quick-filter
  // and export what they show.
  const flatten = list =>
    Array.isArray(list)
      ? list.map(r => ({
        ...r,
        name: name(r),
        owner: connectionOwner(r),
        statusRank: r.active ? 0 : 1,
        offered: r.active ? 'Offered to everyone' : 'Switched off',
        reach: r.active ? 'Active, reaches nobody' : 'Inactive'
      }))
      : list

  const publicList = useGridList(useMemo(() => flatten(publicRows), [publicRows]))
  const unreachableList = useGridList(useMemo(() => flatten(unreachable), [unreachable]))

  const target = confirm && confirm.row

  return (
    <BreadcrumbView Icon={IconProvider} text="SIGN-IN METHODS">
      <FleetPage
        tabs
        title="Sign-in methods"
        purpose="The sign-in buttons offered to everyone, whatever their email address. An organization's own connection isn't one of these: people reach it through their email domain.">
        {status && <p className="sw-status text-start mb-3" role="status">{status}</p>}
        {error && <p className="text-danger small" role="alert">{error}</p>}
        {error && <ErrorReference error={failure} />}
        {rows === null && <p className="text-danger small" role="alert">The sign-in methods couldn't be loaded. Reload to try again.</p>}

        <FleetSection id="public" title="Public sign-in methods"
          lede="Every active one appears on the sign-in page for everyone. Whoever it signs in is then admitted by the organization rules of the connection, so only platform methods such as Google, Apple and Facebook belong here. Existing accounts confirm once before a public method signs them in.">
          <FleetGrid flush title="public sign-in methods" tableRef={publicTableRef} exportName="public-sign-in-methods" compact rowHeight={ROW_HEIGHT_TWO_LINE}>
            <TableAgClient
              ref={publicTableRef}
              queryKey="public"
              apiList={publicList}
              onReady={readyFor('public')}
              sortCol="name"
              rowHeight={ROW_HEIGHT_TWO_LINE}
              emptyState={{
                Icon: IconProvider,
                title: 'No public sign-in methods',
                message: "People sign in with their password or through their organization's own connection. To offer one to everyone, make it public below."
              }}>
              <ColumnFormat field="name" header="Sign-in method" render={connectionCell} stacked minWidth={240} flex={2} sortable />
              <ColumnSelector field="offered" header="On the sign-in page" selector={r => <Pill tone={r.active ? 'ok' : null}>{r.offered}</Pill>} comparator={byStatus} sortable />
              <ColumnMenu
                menu={r =>
                  <Menu small>
                    <MenuItem Icon={r.active ? IconUnactivate : IconActivate} text={r.active ? 'switch off' : 'switch on'} pin show={canEdit} disabled={busy} onClick={() => toggleActive(r)} />
                    <MenuItem Icon={IconRemove} text="stop offering to everyone" pin show={canEdit} disabled={busy} onClick={() => { setConfirmError(null); setConfirm({ row: r, public: false }) }} />
                  </Menu>}
              />
            </TableAgClient>
          </FleetGrid>
        </FleetSection>

        <FleetSection id="unreachable" title="Not offered to anyone"
          lede="Connections with no email domain bound and not marked public. Nobody is sent to them. If one belongs to an organization, bind its domain; make it public only if it is a platform method meant for everyone.">
          <FleetGrid flush title="not offered to anyone" tableRef={unreachableTableRef} exportName="connections-not-offered" compact rowHeight={ROW_HEIGHT_TWO_LINE}>
            <TableAgClient
              ref={unreachableTableRef}
              queryKey="unreachable"
              apiList={unreachableList}
              onReady={readyFor('unreachable')}
              sortCol="name"
              rowHeight={ROW_HEIGHT_TWO_LINE}
              emptyState={{
                Icon: IconProvider,
                title: 'Every connection is reachable',
                message: 'Every connection is either public or reached through a bound domain.'
              }}>
              <ColumnFormat field="name" header="Connection" render={connectionCell} stacked minWidth={240} flex={2} sortable />
              <ColumnText field="owner" header="Owner" sortable />
              <ColumnSelector field="reach" header="Status" selector={r => <Pill tone={r.active ? 'warn' : null}>{r.reach}</Pill>} comparator={byStatus} sortable />
              <ColumnMenu
                menu={r =>
                  <Menu small>
                    <MenuItem Icon={IconDomain} text="bind a domain" to={`/admin/provider/list/edit/${r.id}#routing`} pin show />
                    <MenuItem Icon={IconAdd} text="make public" pin show={canEdit} disabled={busy} onClick={() => { setConfirmError(null); setConfirm({ row: r, public: true }) }} />
                  </Menu>}
              />
            </TableAgClient>
          </FleetGrid>
        </FleetSection>

        <Notice>
          <p>New connections start inactive and private. Nothing appears on the sign-in page for everyone unless it's marked public here.</p>
        </Notice>
      </FleetPage>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={target ? (confirm.public ? `Offer ${name(target)} to everyone?` : `Stop offering ${name(target)} to everyone?`) : ''}
        body={target
          ? confirm.public
            ? `${name(target)} ${target.active ? 'appears' : 'will appear, once switched on,'} as a sign-in button for every person on the sign-in page, whatever their email. Anyone its identity provider signs in is then admitted by its organization rules. Existing accounts confirm once before this method signs them in. Only do this for a platform method such as Google, Apple or Facebook, never an organization's connection.`
            : `${name(target)} disappears from the sign-in page. People can still reach it through any email domain bound to it.`
          : ''}
        confirmText={confirm && confirm.public ? 'Make public' : 'Stop offering'}
        busy={busy}
        error={confirmError}
        failure={confirmFailure}
        onConfirm={setPublic}
        onCancel={() => setConfirm(null)}
      />
    </BreadcrumbView>
  )
}

const connectionCell = cell => {
  const row = cell.data
  if (!row) return ''
  return (
    <span className="d-inline-flex align-items-center gap-2">
      <span className="d-none d-sm-inline-flex"><ProviderLogo iconSVG={row.iconSVG} /></span>
      <span className="min-w-0">
        <Link to={`/admin/provider/list/edit/${row.id}`} className="fw-semibold">{name(row)}</Link>
        <span className="sw-cell-sub">{connectionKind(row)}</span>
      </span>
    </span>
  )
}

// Active first, as the status columns always sorted.
const byStatus = (a, b, nodeA, nodeB) => (nodeA && nodeA.data ? nodeA.data.statusRank : 0) - (nodeB && nodeB.data ? nodeB.data.statusRank : 0)
